import { createAuditLog } from './supabase/audit-logs';
import { QueryClient } from '@tanstack/react-query';

// Define standard action types for consistency
export const AuditActionTypes = {
  // Authentication actions
  LOGIN: 'User Login',
  LOGOUT: 'User Logout',
  
  // View actions
  VIEW_RECORD: 'View Record',
  VIEW_PAGE: 'View Page',
  
  // Data modification actions
  CREATE_RECORD: 'Create Record',
  UPDATE_RECORD: 'Update Record',
  DELETE_RECORD: 'Delete Record',
  
  // Export actions
  EXPORT_DATA: 'Export Data',
  
  // Search actions
  SEARCH: 'Search',
  
  // Filter actions
  APPLY_FILTER: 'Apply Filter',
  
  // System actions
  SYSTEM_ERROR: 'System Error',
  API_REQUEST: 'API Request'
};

// Maximum number of retry attempts
const MAX_RETRIES = 3;

/**
 * Log a user action with retry capability
 * @param userName The user's email or identifier
 * @param action The action being performed (use AuditActionTypes for consistency)
 * @param details Details about the action
 * @param entityType Optional entity type (e.g., 'Lease', 'Sublease', etc.)
 * @param entityId Optional entity identifier
 * @param queryClient Optional query client for invalidating queries
 * @param retryCount Internal parameter for tracking retry attempts
 */
export async function logAction(
  userName: string,
  action: string, 
  details: string, 
  entityType?: string, 
  entityId?: string | number,
  queryClient?: QueryClient,
  retryCount = 0
) {
  const isTestUser = userName.toLowerCase() === 'mattssubscriptions@gmail.com';
  
  // Format details with entity information if provided
  let formattedDetails = details;
  if (entityType) {
    formattedDetails = entityId 
      ? `${entityType} ID: ${entityId} - ${details}`
      : `${entityType} - ${details}`;
  }
  
  // Add special handling for test user to ensure their actions are logged
  if (isTestUser) {
    console.log(`Test user action detected: ${action} - ${formattedDetails}`);
    // Add timestamp to make the details unique for test user
    formattedDetails = `${formattedDetails} (${new Date().toISOString()})`;
  }

  try {
    console.log(`Logging action "${action}" for user: ${userName}`);
    await createAuditLog({
      action,
      user_name: userName,
      details: formattedDetails,
      source: 'custom' // Explicitly mark as application log
    });
    console.log(`Successfully logged action "${action}" for user: ${userName}`);
    
    // Invalidate audit logs query to ensure the UI shows the latest logs
    if (queryClient) {
      console.log('Invalidating audit-logs query cache');
      try {
        // Use more aggressive invalidation to ensure the cache is cleared
        await queryClient.invalidateQueries({ 
          queryKey: ['audit-logs'],
          refetchType: 'all'
        });
        console.log('Successfully invalidated audit-logs query cache');
      } catch (invalidateError) {
        console.error('Error invalidating query cache:', invalidateError);
      }
    } else {
      console.warn('No queryClient provided, audit logs UI may not update automatically');
    }
  } catch (error) {
    console.error(`Failed to create audit log for action "${action}" (attempt ${retryCount + 1}):`, error);
    
    // Retry logic for all users, not just test users
    if (retryCount < MAX_RETRIES) {
      const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
      console.log(`Retrying audit log creation in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
      
      setTimeout(() => {
        logAction(
          userName,
          action, 
          `${formattedDetails} (retry ${retryCount + 1})`, 
          entityType, 
          entityId,
          queryClient,
          retryCount + 1
        );
      }, delay);
    } else {
      console.error(`Maximum retry attempts (${MAX_RETRIES}) reached for audit log creation`);
    }
  }
}

/**
 * Convenience method for logging view actions
 */
export function logView(
  userName: string,
  entityType: string, 
  entityId?: string | number, 
  details?: string,
  queryClient?: QueryClient
) {
  const viewDetails = details || `Viewed ${entityType}${entityId ? ` #${entityId}` : ''}`;
  return logAction(userName, AuditActionTypes.VIEW_RECORD, viewDetails, entityType, entityId, queryClient);
}

/**
 * Convenience method for logging create actions
 */
export function logCreate(
  userName: string,
  entityType: string, 
  entityId?: string | number, 
  details?: string,
  queryClient?: QueryClient
) {
  const createDetails = details || `Created new ${entityType}${entityId ? ` #${entityId}` : ''}`;
  return logAction(userName, AuditActionTypes.CREATE_RECORD, createDetails, entityType, entityId, queryClient);
}

/**
 * Convenience method for logging update actions
 */
export function logUpdate(
  userName: string,
  entityType: string, 
  entityId?: string | number, 
  details?: string,
  queryClient?: QueryClient
) {
  const updateDetails = details || `Updated ${entityType}${entityId ? ` #${entityId}` : ''}`;
  return logAction(userName, AuditActionTypes.UPDATE_RECORD, updateDetails, entityType, entityId, queryClient);
}

/**
 * Convenience method for logging delete actions
 */
export function logDelete(
  userName: string,
  entityType: string, 
  entityId?: string | number, 
  details?: string,
  queryClient?: QueryClient
) {
  const deleteDetails = details || `Deleted ${entityType}${entityId ? ` #${entityId}` : ''}`;
  return logAction(userName, AuditActionTypes.DELETE_RECORD, deleteDetails, entityType, entityId, queryClient);
}

/**
 * Log a login action
 */
export function logLogin(
  userName: string,
  details?: string,
  queryClient?: QueryClient
) {
  const loginDetails = details || `Successful login from ${window.location.hostname}`;
  return logAction(userName, AuditActionTypes.LOGIN, loginDetails, 'Authentication', undefined, queryClient);
}

/**
 * Log a logout action
 */
export function logLogout(
  userName: string,
  details?: string,
  queryClient?: QueryClient
) {
  const logoutDetails = details || `User logged out from ${window.location.hostname}`;
  return logAction(userName, AuditActionTypes.LOGOUT, logoutDetails, 'Authentication', undefined, queryClient);
}
